import { StateCreator } from 'zustand';
import { QuotationStore } from '../quotationStore.types';
import {
    LineItem,
    LineItemSubItem,
    QuotationSection,
    RoomInstance,
    ScopeGroupType,
} from '@/types/quotation';
import { generateId } from '@/lib/utils';
import {
    generateRoomDisplayName,
    type CataloguePresetItem,
} from '@/types/roomMaster';
import { industryService, SessionExpiredError } from '@/services/industryService';
import { quotationService } from '@/services/quotationService';
import { settingsService } from '@/services/settingsService';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import {
    calculateLocationRollup,
    calculateLocationSubTotal,
    createDefaultLocationSubItem,
} from '@/utils/locationSubItems';

// Backward-compat alias
type RoomCategory = ScopeGroupType;

export interface RoomManagementSlice {
    addRoomToQuotation: (category: ScopeGroupType, customName?: string) => void;
    removeRoomFromQuotation: (roomId: string) => void;
    reorderRooms: (roomIds: string[]) => void;
    removeRoomFromSection: (sectionId: string, roomId: string) => void;
    addSection: (name: string, workType: string) => Promise<void>;
    appendAiSections: (sections: QuotationSection[]) => void;
    updateSection: (sectionId: string, updates: Partial<QuotationSection>) => void;
    removeSection: (sectionId: string) => void;
    toggleSectionGst: (sectionId: string, enabled: boolean) => void;
    toggleGlobalGst: (enabled: boolean) => void;
    /** Load catalogue presets for a single scope group within a section */
    loadRoomPrebuilts: (sectionId: string, roomId: string) => Promise<void>;
    /** Load catalogue presets for ALL scope groups within a section */
    loadSectionPrebuilts: (sectionId: string) => Promise<void>;
    addLineItem: (sectionId: string, roomId: string, item: Omit<LineItem, 'id' | 'slNo'>) => void;
    updateLineItem: (sectionId: string, roomId: string, itemId: string, updates: Partial<LineItem>) => void;
    removeLineItem: (sectionId: string, roomId: string, itemId: string) => void;
    reorderLineItems: (sectionId: string, roomId: string, itemIds: string[]) => void;
    resetSectionValues: (sectionId: string) => void;
    resetQuotationValues: () => void;
    updateRoomName: (roomId: string, newName: string) => void;
    addSubItem: (sectionId: string, roomId: string, itemId: string) => void;
    updateSubItem: (sectionId: string, roomId: string, itemId: string, subItemId: string, updates: Partial<LineItemSubItem>) => void;
    removeSubItem: (sectionId: string, roomId: string, itemId: string, subItemId: string) => void;
    loadFromQuotationTemplate: (
        templateQuote: Quotation,
        options?: {
            mode?: 'replace' | 'append';
            selectedRoomIds?: string[];
            selectedSectionIds?: string[];
            selectedSectionRoomMap?: Record<string, string[]>;
            syncRatesWithCatalog?: boolean;
        }
    ) => Promise<void>;
}

export const createRoomManagementSlice: StateCreator<
    QuotationStore,
    [],
    [],
    RoomManagementSlice
> = (set, get) => ({
    addRoomToQuotation: (category, customName) => {
        const { currentQuotation, sessionUnlocked } = get();
        if (!currentQuotation) return;

        const isLocked = currentQuotation.metadata.locked || currentQuotation.metadata.status === 'finalized';
        if (isLocked && !sessionUnlocked) return;

        const roomCount = currentQuotation.rooms.filter(r => r.category === category).length;
        // generateRoomDisplayName now reads ScopeGroupDefinition from settings
        const baseName = customName || generateRoomDisplayName(category, roomCount + 1);

        const newRoom: RoomInstance = {
            id: generateId(),
            category,
            instanceNumber: roomCount + 1,
            displayName: baseName,
            customName: customName,
        };

        const updatedRooms = [...currentQuotation.rooms, newRoom];
        const updatedSections = currentQuotation.sections.map(section => ({
            ...section,
            roomGroups: [
                ...section.roomGroups,
                {
                    roomId: newRoom.id,
                    roomName: newRoom.displayName,
                    items: [],
                    subtotal: 0
                }
            ]
        }));

        set({
            currentQuotation: {
                ...currentQuotation,
                rooms: updatedRooms,
                sections: updatedSections
            }
        });
    },

    removeRoomFromQuotation: (roomId) => {
        const { currentQuotation, sessionUnlocked } = get();
        if (!currentQuotation) return;

        const isLocked = currentQuotation.metadata.locked || currentQuotation.metadata.status === 'finalized';
        if (isLocked && !sessionUnlocked) return;

        const updatedRooms = currentQuotation.rooms.filter(r => r.id !== roomId);
        const updatedSections = currentQuotation.sections.map(section => ({
            ...section,
            roomGroups: section.roomGroups.filter(rg => rg.roomId !== roomId)
        }));

        set({
            currentQuotation: {
                ...currentQuotation,
                rooms: updatedRooms,
                sections: updatedSections
            }
        });
        get().recalculateFinancials();
    },

    reorderRooms: (roomIds) => {
        const { currentQuotation, sessionUnlocked } = get();
        if (!currentQuotation) return;

        const isLocked = currentQuotation.metadata.locked || currentQuotation.metadata.status === 'finalized';
        if (isLocked && !sessionUnlocked) return;

        const updatedRooms = roomIds
            .map(id => currentQuotation.rooms.find(r => r.id === id))
            .filter((r): r is RoomInstance => !!r);

        const updatedSections = currentQuotation.sections.map(section => {
            const updatedGroups = [...section.roomGroups].sort((a, b) => {
                return roomIds.indexOf(a.roomId) - roomIds.indexOf(b.roomId);
            });

            return { ...section, roomGroups: updatedGroups };
        });

        set({
            currentQuotation: {
                ...currentQuotation,
                rooms: updatedRooms,
                sections: updatedSections
            }
        });
    },

    removeRoomFromSection: (sectionId, roomId) => {
        const { currentQuotation, sessionUnlocked } = get();
        if (!currentQuotation) return;

        const isLocked = currentQuotation.metadata.locked || currentQuotation.metadata.status === 'finalized';
        if (isLocked && !sessionUnlocked) return;

        const updatedSections = currentQuotation.sections.map(section => {
            if (section.id === sectionId) {
                return {
                    ...section,
                    roomGroups: section.roomGroups.filter(rg => rg.roomId !== roomId)
                };
            }
            return section;
        });

        set({
            currentQuotation: {
                ...currentQuotation,
                sections: updatedSections
            }
        });
        get().recalculateFinancials();
    },

    addSection: async (name, workType) => {
        const { currentQuotation, sessionUnlocked } = get();
        if (!currentQuotation) return;

        const isLocked = currentQuotation.metadata.locked || currentQuotation.metadata.status === 'finalized';
        if (isLocked && !sessionUnlocked) return;

        set({ past: [...get().past, currentQuotation], future: [] });

        // Build empty section first — populate items below via catalogue
        const newSection: QuotationSection = {
            id: generateId(),
            name,
            workType,
            gstEnabled: false,
            sectionSubtotal: 0,
            roomGroups: currentQuotation.rooms.map(room => ({
                roomId: room.id,
                roomName: room.displayName,
                items: [],
                subtotal: 0,
            })),
        };

        // For each scope group, query catalogue via industryService
        try {
            const roomGroupsWithItems = await Promise.all(
                newSection.roomGroups.map(async (group) => {
                    const room = currentQuotation.rooms.find(r => r.id === group.roomId);
                    if (!room) return group;

                    const allPresets = await industryService.getCataloguePresets(
                        room.category,
                        workType,
                        currentQuotation.metadata.industryId
                    );

                    const strict  = allPresets.filter((i: unknown) => (i as CataloguePresetItem).match_tier === 'strict');
                    const byScope = allPresets.filter((i: unknown) => (i as CataloguePresetItem).match_tier === 'by_scope');
                    const candidates = strict.length > 0 ? strict : byScope;

                    const items: LineItem[] = candidates.map((item: CataloguePresetItem, idx: number) => ({
                        id: generateId(),
                        slNo: idx + 1,
                        description: item.name,
                        specifications: item.specifications || '',
                        quantity: 0,
                        uom: item.default_uom,
                        rate: item.default_rate,
                        total: 0,
                        catalog_item_id: item.id,
                        catalogCategory: item.category,
                        dimensions: item.has_dimensions
                            ? { width: 0, height: 0, unit: 'cm' as const, sqft: 0 }
                            : undefined,
                    }));

                    return { ...group, items };
                })
            );

            set({
                currentQuotation: {
                    ...currentQuotation,
                    sections: [
                        ...currentQuotation.sections,
                        { ...newSection, roomGroups: roomGroupsWithItems },
                    ],
                },
            });

            get().recalculateFinancials();
        } catch (error) {
            if (error instanceof SessionExpiredError) {
                toast.error(error.message);
                await supabase.auth.signOut();
                window.location.href = '/login';
            } else {
                // Revert to past state
                const { past } = get();
                const previousQuotation = past[past.length - 1];
                if (previousQuotation) {
                    set({ currentQuotation: previousQuotation, past: past.slice(0, -1) });
                }
                console.error('Failed to add section', error);
                toast.error('Failed to load catalogue items. Please check your connection and try again.');
            }
        }
    },

    appendAiSections: (sections) => {
        const { currentQuotation } = get();
        if (!currentQuotation) return;
        set({
            currentQuotation: {
                ...currentQuotation,
                sections: [...currentQuotation.sections, ...sections],
            },
        });
        get().recalculateFinancials();
    },

    updateSection: (sectionId, updates) => {
        const { currentQuotation, sessionUnlocked } = get();
        if (!currentQuotation) return;

        const isLocked = currentQuotation.metadata.locked || currentQuotation.metadata.status === 'finalized';
        if (isLocked && !sessionUnlocked) return;

        const updatedSections = currentQuotation.sections.map((section) =>
            section.id === sectionId ? { ...section, ...updates } : section
        );

        set({ currentQuotation: { ...currentQuotation, sections: updatedSections } });
        get().recalculateFinancials();
    },

    removeSection: (sectionId) => {
        const { currentQuotation, sessionUnlocked } = get();
        if (!currentQuotation) return;

        const isLocked = currentQuotation.metadata.locked || currentQuotation.metadata.status === 'finalized';
        if (isLocked && !sessionUnlocked) return;

        const updatedSections = currentQuotation.sections.filter(
            (section) => section.id !== sectionId
        );

        set({ currentQuotation: { ...currentQuotation, sections: updatedSections } });
        get().recalculateFinancials();
    },

    toggleSectionGst: (sectionId, enabled) => {
        const { currentQuotation, sessionUnlocked } = get();
        if (!currentQuotation) return;

        const isLocked = currentQuotation.metadata.locked || currentQuotation.metadata.status === 'finalized';
        if (isLocked && !sessionUnlocked) return;

        const updatedSections = currentQuotation.sections.map((section) =>
            section.id === sectionId ? { ...section, gstEnabled: enabled } : section
        );

        set({ currentQuotation: { ...currentQuotation, sections: updatedSections } });
        get().recalculateFinancials();
    },

    toggleGlobalGst: (enabled) => {
        const { currentQuotation, sessionUnlocked } = get();
        if (!currentQuotation) return;

        if (currentQuotation.metadata?.locked && !sessionUnlocked) return;

        const updatedSections = currentQuotation.sections.map((section) => ({
            ...section,
            gstEnabled: enabled
        }));

        set({ currentQuotation: { ...currentQuotation, sections: updatedSections } });
        get().recalculateFinancials();
    },

    loadRoomPrebuilts: async (sectionId, roomId) => {
        const { currentQuotation, sessionUnlocked } = get();
        if (!currentQuotation) return;

        const isLocked = currentQuotation.metadata.locked || currentQuotation.metadata.status === 'finalized';
        if (isLocked && !sessionUnlocked) return;

        const section = currentQuotation.sections.find(s => s.id === sectionId);
        if (!section) return;

        const room = currentQuotation.rooms.find(r => r.id === roomId);
        if (!room) return;

        // Query catalogue via industryService (uses DB function fn_get_catalogue_presets)
        const allPresets = await industryService.getCataloguePresets(
            room.category,
            section.workType,
            currentQuotation.metadata.industryId
        );

        // Separate strict matches from scope-only fallbacks
        const strict   = allPresets.filter((i: any) => i.match_tier === 'strict');
        const candidates: CataloguePresetItem[] = strict
            .map((i: any) => ({
                id:             i.id,
                name:           i.name,
                description:    i.description,
                specifications: i.specifications,
                category:       i.category,
                room_type:      i.room_type,
                default_rate:   i.default_rate,
                default_uom:    i.default_uom,
                has_dimensions: i.has_dimensions,
            }));

        candidates.forEach((item: CataloguePresetItem) => {
            get().addLineItem(sectionId, roomId, {
                description: item.name,
                specifications: item.specifications || '',
                quantity: 0,
                uom: item.default_uom,
                rate: item.default_rate,
                total: 0,
                catalog_item_id: item.id,
                catalogCategory: item.category,
                dimensions: item.has_dimensions
                    ? { width: 0, height: 0, unit: 'cm' as const, sqft: 0 }
                    : undefined,
            });
        });
    },

    loadSectionPrebuilts: async (sectionId) => {
        const { currentQuotation, sessionUnlocked } = get();
        if (!currentQuotation) return;

        const isLocked = currentQuotation.metadata.locked || currentQuotation.metadata.status === 'finalized';
        if (isLocked && !sessionUnlocked) return;

        const section = currentQuotation.sections.find(s => s.id === sectionId);
        if (!section) return;

        // Load presets for all scope groups in parallel
        await Promise.all(
            section.roomGroups.map(group =>
                get().loadRoomPrebuilts(sectionId, group.roomId)
            )
        );
    },

    addLineItem: (sectionId, roomId, item) => {
        const { currentQuotation, sessionUnlocked } = get();
        if (!currentQuotation) return;

        const isLocked = currentQuotation.metadata.locked || currentQuotation.metadata.status === 'finalized';
        if (isLocked && !sessionUnlocked) return;

        set({ past: [...get().past, currentQuotation], future: [] });
        const newItem: LineItem = { ...item, id: generateId(), slNo: 0 };

        const updatedSections = currentQuotation.sections.map((section) => {
            if (section.id === sectionId) {
                const updatedGroups = section.roomGroups.map(group => {
                    if (group.roomId === roomId) {
                        const updatedItems = [...group.items, newItem].map((it, idx) => ({
                            ...it,
                            slNo: idx + 1
                        }));
                        return {
                            ...group,
                            items: updatedItems,
                            subtotal: updatedItems.reduce((sum, it) => sum + Number(it.total || 0), 0)
                        };
                    }
                    return group;
                });
                return {
                    ...section,
                    roomGroups: updatedGroups,
                    sectionSubtotal: updatedGroups.reduce((sum, g) => sum + Number(g.subtotal || 0), 0)
                };
            }
            return section;
        });

        set({ currentQuotation: { ...currentQuotation, sections: updatedSections } });
        get().triggerRecalculateDebounced();
    },

    updateLineItem: (sectionId, roomId, itemId, updates) => {
        const { currentQuotation, sessionUnlocked } = get();
        if (!currentQuotation) return;

        const isLocked = currentQuotation.metadata.locked || currentQuotation.metadata.status === 'finalized';
        if (isLocked && !sessionUnlocked) return;

        const updatedSections = currentQuotation.sections.map((section) => {
            if (section.id === sectionId) {
                const updatedGroups = section.roomGroups.map(group => {
                    if (group.roomId === roomId) {
                        const updatedItems = group.items.map(item => {
                            if (item.id !== itemId) return item;
                            
                            const newItem = { ...item, ...updates };
                            
                            // If rate changed, we MUST update sub-items that inherit this rate
                            if (updates.rate !== undefined && newItem.subItems && newItem.subItems.length > 0) {
                                newItem.subItems = newItem.subItems.map(si => {
                                    if (si.rate !== undefined) return si; // Has custom rate, skip

                                    return {
                                        ...si,
                                        subTotal: calculateLocationSubTotal(si, updates.rate!),
                                    };
                                });

                                const rollup = calculateLocationRollup(newItem.subItems);
                                newItem.total = rollup.total;
                                newItem.quantity = rollup.quantity;
                            }
                            
                            return newItem;
                        });
                        return {
                            ...group,
                            items: updatedItems,
                            subtotal: updatedItems.reduce((sum, it) => sum + Number(it.total || 0), 0)
                        };
                    }
                    return group;
                });
                return {
                    ...section,
                    roomGroups: updatedGroups,
                    sectionSubtotal: updatedGroups.reduce((sum, g) => sum + Number(g.subtotal || 0), 0)
                };
            }
            return section;
        });

        set({ currentQuotation: { ...currentQuotation, sections: updatedSections } });
        get().triggerRecalculateDebounced();
    },

    removeLineItem: (sectionId, roomId, itemId) => {
        const { currentQuotation, sessionUnlocked } = get();
        if (!currentQuotation) return;

        const isLocked = currentQuotation.metadata.locked || currentQuotation.metadata.status === 'finalized';
        if (isLocked && !sessionUnlocked) return;

        const updatedSections = currentQuotation.sections.map((section) => {
            if (section.id === sectionId) {
                const updatedGroups = section.roomGroups.map(group => {
                    if (group.roomId === roomId) {
                        const updatedItems = group.items
                            .filter(item => item.id !== itemId)
                            .map((it, idx) => ({ ...it, slNo: idx + 1 }));
                        return {
                            ...group,
                            items: updatedItems,
                            subtotal: updatedItems.reduce((sum, it) => sum + Number(it.total || 0), 0)
                        };
                    }
                    return group;
                });
                return {
                    ...section,
                    roomGroups: updatedGroups,
                    sectionSubtotal: updatedGroups.reduce((sum, g) => sum + Number(g.subtotal || 0), 0)
                };
            }
            return section;
        });

        set({ currentQuotation: { ...currentQuotation, sections: updatedSections } });
        get().recalculateFinancials();
    },

    reorderLineItems: (sectionId, roomId, itemIds) => {
        const { currentQuotation, sessionUnlocked } = get();
        if (!currentQuotation) return;

        const isLocked = currentQuotation.metadata.locked || currentQuotation.metadata.status === 'finalized';
        if (isLocked && !sessionUnlocked) return;

        const updatedSections = currentQuotation.sections.map((section) => {
            if (section.id === sectionId) {
                const updatedGroups = section.roomGroups.map(group => {
                    if (group.roomId === roomId) {
                        const itemMap = new Map(group.items.map(item => [item.id, item]));
                        const reorderedItems = itemIds
                            .map(id => itemMap.get(id))
                            .filter((item): item is LineItem => item !== undefined)
                            .map((item, idx) => ({
                                ...item,
                                slNo: idx + 1,
                                sort_order: idx
                            }));

                        return {
                            ...group,
                            items: reorderedItems,
                            subtotal: reorderedItems.reduce((sum, it) => sum + it.total, 0)
                        };
                    }
                    return group;
                });
                return {
                    ...section,
                    roomGroups: updatedGroups,
                    sectionSubtotal: updatedGroups.reduce((sum, g) => sum + g.subtotal, 0)
                };
            }
            return section;
        });

        set({ currentQuotation: { ...currentQuotation, sections: updatedSections } });
        get().recalculateFinancials();
    },

    resetSectionValues: (sectionId) => {
        const { currentQuotation, sessionUnlocked } = get();
        if (!currentQuotation) return;

        const isLocked = currentQuotation.metadata.locked || currentQuotation.metadata.status === 'finalized';
        if (isLocked && !sessionUnlocked) return;

        const updatedSections = currentQuotation.sections.map((section) => {
            if (section.id === sectionId) {
                const updatedGroups = section.roomGroups.map(group => ({
                    ...group,
                    items: group.items.map(item => ({
                        ...item,
                        quantity: 0,
                        total: 0,
                        dimensions: item.dimensions ? { ...item.dimensions, width: 0, height: 0, sqft: 0 } : undefined
                    })),
                    subtotal: 0
                }));
                return {
                    ...section,
                    roomGroups: updatedGroups,
                    sectionSubtotal: 0
                };
            }
            return section;
        });

        set({ currentQuotation: { ...currentQuotation, sections: updatedSections } });
        get().recalculateFinancials();
    },

    resetQuotationValues: () => {
        const { currentQuotation, sessionUnlocked } = get();
        if (!currentQuotation) return;

        const isLocked = currentQuotation.metadata.locked || currentQuotation.metadata.status === 'finalized';
        if (isLocked && !sessionUnlocked) return;

        const updatedSections = currentQuotation.sections.map((section) => ({
            ...section,
            roomGroups: section.roomGroups.map(group => ({
                ...group,
                items: group.items.map(item => ({
                    ...item,
                    quantity: 0,
                    total: 0,
                    dimensions: item.dimensions ? { ...item.dimensions, width: 0, height: 0, sqft: 0 } : undefined
                })),
                subtotal: 0
            })),
            sectionSubtotal: 0
        }));

        set({ currentQuotation: { ...currentQuotation, sections: updatedSections } });
        get().recalculateFinancials();
    },

    updateRoomName: (roomId, newName) => {
        const { currentQuotation, sessionUnlocked } = get();
        if (!currentQuotation) return;

        const isLocked = currentQuotation.metadata.locked || currentQuotation.metadata.status === 'finalized';
        if (isLocked && !sessionUnlocked) return;

        const updatedRooms = currentQuotation.rooms.map(room =>
            room.id === roomId ? { ...room, displayName: newName, customName: newName } : room
        );
        const updatedSections = currentQuotation.sections.map(section => ({
            ...section,
            roomGroups: section.roomGroups.map(group =>
                group.roomId === roomId ? { ...group, roomName: newName } : group
            )
        }));

        set({ currentQuotation: { ...currentQuotation, rooms: updatedRooms, sections: updatedSections } });
    },

    addSubItem: (sectionId, roomId, itemId) => {
        const { currentQuotation, sessionUnlocked } = get();
        if (!currentQuotation) return;

        const isLocked = currentQuotation.metadata.locked || currentQuotation.metadata.status === 'finalized';
        if (isLocked && !sessionUnlocked) return;

        // Find the parent item to inherit its UOM
        const parentItem = currentQuotation.sections
            .find(s => s.id === sectionId)
            ?.roomGroups.find(g => g.roomId === roomId)
            ?.items.find(i => i.id === itemId);
        const roomGroup = currentQuotation.sections
            .find(s => s.id === sectionId)
            ?.roomGroups.find(g => g.roomId === roomId);

        if (!parentItem) return;

        const newSubItem: LineItemSubItem = createDefaultLocationSubItem(
            parentItem,
            (parentItem.subItems?.length || 0) + 1,
            roomGroup?.roomName,
        );
        newSubItem.subTotal = calculateLocationSubTotal(newSubItem, parentItem.rate || 0);

        const updatedSections = currentQuotation.sections.map(section => {
            if (section.id !== sectionId) return section;
            return {
                ...section,
                roomGroups: section.roomGroups.map(group => {
                    if (group.roomId !== roomId) return group;
                    return {
                        ...group,
                        items: group.items.map(item => {
                            if (item.id !== itemId) return item;
                            const updatedSubItems = [...(item.subItems || []), newSubItem];
                            const rollup = calculateLocationRollup(updatedSubItems);
                            return {
                                ...item,
                                subItems: updatedSubItems,
                                quantity: rollup.quantity,
                                total: rollup.total,
                            };
                        })
                    };
                })
            };
        });

        set({ currentQuotation: { ...currentQuotation, sections: updatedSections } });
    },

    updateSubItem: (sectionId, roomId, itemId, subItemId, updates) => {
        const { currentQuotation, sessionUnlocked } = get();
        if (!currentQuotation) return;

        const isLocked = currentQuotation.metadata.locked || currentQuotation.metadata.status === 'finalized';
        if (isLocked && !sessionUnlocked) return;

        const updatedSections = currentQuotation.sections.map(section => {
            if (section.id !== sectionId) return section;
            const updatedRoomGroups = section.roomGroups.map(group => {
                if (group.roomId !== roomId) return group;
                const updatedItems = group.items.map(item => {
                    if (item.id !== itemId) return item;
                    const updatedSubItems = (item.subItems || []).map(si => {
                        if (si.id !== subItemId) return si;
                        const nextSubItem = { ...si, ...updates };
                        return {
                            ...nextSubItem,
                            subTotal: updates.subTotal !== undefined
                                ? updates.subTotal
                                : calculateLocationSubTotal(nextSubItem, item.rate || 0),
                        };
                    });
                    const rollup = calculateLocationRollup(updatedSubItems);
                    return {
                        ...item,
                        subItems: updatedSubItems,
                        quantity: rollup.quantity,
                        total: rollup.total,
                    };
                });
                return {
                    ...group,
                    items: updatedItems,
                    subtotal: updatedItems.reduce((s, i) => s + (i.total || 0), 0)
                };
            });
            return {
                ...section,
                roomGroups: updatedRoomGroups,
                sectionSubtotal: updatedRoomGroups.reduce((s, g) => s + (g.subtotal || 0), 0)
            };
        });

        set({ currentQuotation: { ...currentQuotation, sections: updatedSections } });
        get().triggerRecalculateDebounced();
    },

    removeSubItem: (sectionId, roomId, itemId, subItemId) => {
        const { currentQuotation, sessionUnlocked } = get();
        if (!currentQuotation) return;

        if (currentQuotation.metadata.locked && !sessionUnlocked) return;

        const updatedSections = currentQuotation.sections.map(section => {
            if (section.id !== sectionId) return section;
            const updatedRoomGroups = section.roomGroups.map(group => {
                if (group.roomId !== roomId) return group;
                const updatedItems = group.items.map(item => {
                    if (item.id !== itemId) return item;
                    const updatedSubItems = (item.subItems || []).filter(si => si.id !== subItemId);
                    const rollup = calculateLocationRollup(updatedSubItems);
                    return {
                        ...item,
                        subItems: updatedSubItems,
                        quantity: rollup.quantity,
                        total: rollup.total,
                    };
                });
                return {
                    ...group,
                    items: updatedItems,
                    subtotal: updatedItems.reduce((s, i) => s + (i.total || 0), 0)
                };
            });
            return {
                ...section,
                roomGroups: updatedRoomGroups,
                sectionSubtotal: updatedRoomGroups.reduce((s, g) => s + (g.subtotal || 0), 0)
            };
        });

        set({ currentQuotation: { ...currentQuotation, sections: updatedSections } });
        get().triggerRecalculateDebounced();
    },

    loadFromQuotationTemplate: async (templateQuote, options = {}) => {
        const { currentQuotation, sessionUnlocked } = get();
        if (!currentQuotation) return;

        const isLocked = currentQuotation.metadata.locked || currentQuotation.metadata.status === 'finalized';
        if (isLocked && !sessionUnlocked) {
            toast.error('Quotation is locked');
            return;
        }

        const mode = options.mode || 'replace';
        const syncRates = options.syncRatesWithCatalog ?? false;
        const selectedRoomIds = options.selectedRoomIds;
        const selectedSectionIds = options.selectedSectionIds;
        const selectedSectionRoomMap = options.selectedSectionRoomMap;

        // Fetch catalog items if sync rates is requested
        let catalogRateMap: Map<string, number> = new Map();
        if (syncRates) {
            try {
                const catalogItems = await quotationService.getCatalogItems();
                (catalogItems || []).forEach((ci: any) => {
                    if (ci.id && typeof ci.default_rate === 'number') {
                        catalogRateMap.set(ci.id, ci.default_rate);
                    }
                });
            } catch (err) {
                console.warn('[RoomManagementSlice] Could not fetch catalog items for rate sync:', err);
            }
        }

        // Map rooms
        let targetRooms: RoomInstance[] = mode === 'replace' ? [] : [...currentQuotation.rooms];
        const templateRoomsToUse = selectedRoomIds && selectedRoomIds.length > 0
            ? templateQuote.rooms.filter(r => selectedRoomIds.includes(r.id) || selectedRoomIds.some(id => id.toLowerCase() === r.displayName.toLowerCase()))
            : templateQuote.rooms;

        templateRoomsToUse.forEach(tr => {
            if (!targetRooms.some(r => r.displayName.toLowerCase() === tr.displayName.toLowerCase())) {
                targetRooms.push({
                    ...tr,
                    id: generateId(),
                });
            }
        });

        // Filter template sections
        const templateSectionsToUse = templateQuote.sections.filter(s => {
            if (selectedSectionRoomMap) {
                const allowedRooms = selectedSectionRoomMap[s.id] || selectedSectionRoomMap[s.name] || selectedSectionRoomMap[Object.keys(selectedSectionRoomMap).find(k => k.toLowerCase() === s.name.toLowerCase()) || ''];
                return allowedRooms !== undefined && allowedRooms.length > 0;
            }
            if (selectedSectionIds && selectedSectionIds.length > 0) {
                return selectedSectionIds.includes(s.id) || selectedSectionIds.some(id => id.toLowerCase() === s.name.toLowerCase());
            }
            return true;
        });

        // Map sections & room groups & items
        const newClonedSections: QuotationSection[] = templateSectionsToUse
            .map(ts => {
                const sectionId = generateId();
                const allowedRoomsForSection = selectedSectionRoomMap
                    ? (selectedSectionRoomMap[ts.id] || selectedSectionRoomMap[ts.name] || selectedSectionRoomMap[Object.keys(selectedSectionRoomMap).find(k => k.toLowerCase() === ts.name.toLowerCase()) || ''])
                    : undefined;

                const roomGroups = ts.roomGroups
                    .filter(rg => {
                        if (allowedRoomsForSection) {
                            return (
                                allowedRoomsForSection.includes(rg.roomId) ||
                                allowedRoomsForSection.includes(rg.roomName) ||
                                allowedRoomsForSection.some(r => r.toLowerCase() === rg.roomName.toLowerCase())
                            );
                        }
                        if (selectedRoomIds && selectedRoomIds.length > 0) {
                            const matchingTemplateRoom = templateQuote.rooms?.find(r => r.displayName.toLowerCase() === rg.roomName.toLowerCase());
                            return (
                                selectedRoomIds.includes(rg.roomId) ||
                                selectedRoomIds.includes(rg.roomName) ||
                                selectedRoomIds.some(id => id.toLowerCase() === rg.roomName.toLowerCase()) ||
                                (matchingTemplateRoom && selectedRoomIds.includes(matchingTemplateRoom.id))
                            );
                        }
                        return true;
                    })
                    .map(rg => {
                        const mappedRoom = targetRooms.find(r => r.displayName.toLowerCase() === rg.roomName.toLowerCase());
                        const roomId = mappedRoom ? mappedRoom.id : generateId();

                        const items: LineItem[] = (rg.items || []).map((item, idx) => {
                            let rate = item.rate || 0;
                            if (syncRates && item.catalog_item_id && catalogRateMap.has(item.catalog_item_id)) {
                                rate = catalogRateMap.get(item.catalog_item_id)!;
                            }
                            const total = (item.quantity || 0) * rate;
                            return {
                                ...item,
                                id: generateId(),
                                slNo: idx + 1,
                                rate,
                                total,
                                subItems: item.subItems?.map(si => ({
                                    ...si,
                                    id: generateId(),
                                })),
                            };
                        });

                        const subtotal = items.reduce((sum, it) => sum + (it.total || 0), 0);
                        return {
                            ...rg,
                            roomId,
                            items,
                            subtotal,
                        };
                    })
                    .filter(rg => rg.items.length > 0 || !selectedSectionRoomMap); // Drop empty room groups if specifically filtered

                const sectionSubtotal = roomGroups.reduce((sum, rg) => sum + (rg.subtotal || 0), 0);
                return {
                    ...ts,
                    id: sectionId,
                    roomGroups,
                    sectionSubtotal,
                };
            })
            .filter(s => s.roomGroups.length > 0);

        let finalSections: QuotationSection[];
        if (mode === 'replace') {
            finalSections = newClonedSections;
        } else {
            // Merge into current sections
            finalSections = [...currentQuotation.sections];
            newClonedSections.forEach(ns => {
                const existing = finalSections.find(s => s.name.toLowerCase() === ns.name.toLowerCase());
                if (existing) {
                    ns.roomGroups.forEach(nrg => {
                        const existingRg = existing.roomGroups.find(rg => rg.roomId === nrg.roomId);
                        if (existingRg) {
                            existingRg.items.push(...nrg.items);
                            existingRg.items.forEach((it, idx) => { it.slNo = idx + 1; });
                            existingRg.subtotal = existingRg.items.reduce((s, it) => s + (it.total || 0), 0);
                        } else {
                            existing.roomGroups.push(nrg);
                        }
                    });
                    existing.sectionSubtotal = existing.roomGroups.reduce((s, rg) => s + (rg.subtotal || 0), 0);
                } else {
                    finalSections.push(ns);
                }
            });
        }

        const updatedProjectRoomCounts: Record<string, number> = {};
        targetRooms.forEach(r => {
            updatedProjectRoomCounts[r.category] = (updatedProjectRoomCounts[r.category] || 0) + 1;
        });

        set({
            past: [...get().past, currentQuotation],
            future: [],
            currentQuotation: {
                ...currentQuotation,
                rooms: targetRooms,
                sections: finalSections,
                client: {
                    ...(currentQuotation.client || {}),
                    projectRoomCounts: updatedProjectRoomCounts,
                },
                paymentSchedule: templateQuote.paymentSchedule?.length ? templateQuote.paymentSchedule : currentQuotation.paymentSchedule,
                terms: templateQuote.terms ? templateQuote.terms : currentQuotation.terms,
            },
        });

        get().triggerRecalculateDebounced();

        // Persist template section & line item changes to database
        try {
            const updated = get().currentQuotation;
            if (updated && updated.metadata.quoteId) {
                await quotationService.saveQuotation(updated);
            }
        } catch (err) {
            console.warn('[RoomManagementSlice] Indirect section save warning:', err);
        }

        toast.success(`Template loaded: ${templateQuote.metadata.templateName || templateQuote.metadata.quoteNumber}`);
    },
});
